<!--
  AddEpisodesView.vue - "Add URL" page.
  Allows users to submit a URL (YouTube, Rumble, etc.) for the server to fetch
  metadata and add episodes. Shows a recent downloads history stored in localStorage,
  with options to copy, reuse, or clear entries.
-->
<template>
  <div>
      <v-card-text>
        <!-- URL submission form; submit triggers startDownload, prevents default page reload -->
        <v-form
          @submit.prevent="startDownload"
          ref="downloadForm"
          v-model="valid"
        >
          <!-- URL input field with validation rules and a clipboard paste button inside -->
          <v-text-field
            v-model="downloadUrl"
            label="Enter a URL (search query, channel, playlist, video...)"
            :rules="urlRules"
            required
            clearable
            class="mb-2"
          >
            <!-- Append-inner slot: paste-from-clipboard icon button -->
            <template v-slot:append-inner>
              <!-- <v-btn @click="pasteClipboard" color="primary" size="x-large" > -->
                <!-- <template v-slot:prepend> -->
                  <v-btn icon="mdi-content-paste" @click="pasteClipboard" class="ml-1"></v-btn>
                  <!-- Paste -->
                <!-- </template> -->
              <!-- </v-btn> -->
            </template>
          </v-text-field>

          <!-- Submit button: full-width, shows loading spinner while request is in flight -->
          <div class="d-flex align-center">
            <v-btn
              type="submit"
              color="primary"
              :loading="isLoading"
              :disabled="!downloadUrl"
              size="large"
              block
            >
              <template v-slot:prepend>
                <v-icon icon="mdi-cloud-download"></v-icon>
                Submit
              </template>
            </v-btn>
          </div>
        </v-form>
      </v-card-text>
      <!-- </v-card> -->

      <!-- Recent Downloads History Section -->
      <!-- Shows a list of recently submitted URLs, stored in localStorage (max 5 items) -->
      <v-card-title class="text-h6">
        <v-icon icon="mdi-history" class="mr-2"></v-icon>
        Added Recently
        <v-spacer></v-spacer>
        <!-- "Clear All" button only visible when there are items in history -->
        <v-btn
          v-if="recentDownloads.length > 0"
          variant="text"
          color="error"
          size="small"
          @click="confirmClearAll"
        >
          Clear All
        </v-btn>
      </v-card-title>

      <v-card-text>
        <!-- List of recent download URLs; clicking an item fills the URL field -->
        <v-list
          v-if="recentDownloads?.length"
          lines="two"
          class="bg-transparent"
        >
          <v-list-item
            v-for="item in recentDownloads"
            :key="item.url"
            @click="downloadUrl = item.url"
          >
            <!-- Show the URL as the title -->
            <v-list-item-title>
              {{ item.url }}
            </v-list-item-title>
            <!-- Show the submission date as a localized string -->
            <v-list-item-subtitle>
              {{ new Date(item.date).toLocaleString() }}
            </v-list-item-subtitle>

            <!-- Append slot: copy-to-clipboard and delete-from-history buttons -->
            <template v-slot:append>
              <div class="d-flex align-center">
                <!-- Copy URL to clipboard; .stop prevents the list-item click -->
                <v-btn
                  variant="text"
                  icon="mdi-content-copy"
                  size="small"
                  @click.stop="copyUrl(item.url)"
                ></v-btn>

                <!-- Delete single item from recent history, with tooltip -->
                <v-tooltip text="Remove from history" location="top">
                  <template v-slot:activator="{ props }">
                    <v-btn
                      v-bind="props"
                      size="small"
                      variant="text"
                      color="error"
                      icon="mdi-delete"
                      @click.stop="removeRecentItem(item.url)"
                    >
                    </v-btn>
                  </template>
                </v-tooltip>
              </div>
            </template>
          </v-list-item>
        </v-list>

        <!-- Empty state when no recent downloads exist -->
        <v-alert v-else type="info" variant="tonal">
          No URL added recently
        </v-alert>
      </v-card-text>

    <!-- Error Snackbar: displays submission errors (e.g., network failures) -->
    <v-snackbar v-model="showError" color="error" :timeout="5000">
      {{ errorMessage }}
      <template v-slot:actions>
        <v-btn variant="text" @click="showError = false"> Close </v-btn>
      </template>
    </v-snackbar>
  </div>

  <!-- Clear All Confirmation Dialog: asks user to confirm before wiping history -->
  <v-dialog v-model="showClearDialog" max-width="400">
    <v-card>
      <v-card-title>Clear All Recent Downloads?</v-card-title>
      <v-card-text>
        This will remove all items from your recent downloads history.
      </v-card-text>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn variant="text" @click="showClearDialog = false">Cancel</v-btn>
        <v-btn color="error" variant="flat" @click="clearAllRecentDownloads"
          >Confirm</v-btn
        >
      </v-card-actions>
    </v-card>
  </v-dialog>

  <!-- General-purpose snackbar for success/info messages (e.g., "URL copied") -->
  <v-snackbar v-model="snackbar.show" :color="snackbar.color" :timeout="5000">
    {{ snackbar.message }}
    <template v-slot:actions>
      <v-btn variant="text" @click="snackbar.show = false"> Close </v-btn>
    </template>
  </v-snackbar>
</template>

<style scoped>
</style>

<script setup>
import { ref, onMounted, computed } from "vue";
import { episodesApi } from "@/api/episodes.js";
import { useLogin } from "../composables/useLogin.js";
import { useLatest } from "@/composables/useLatest.js";
import { useAppMenu } from "@/composables/useAppMenu.js";
import { getCurrentComponentName } from "@/services/componentName.js";

// Shared app menu state — addUrlDialog controls the parent dialog visibility.
// seguiJobMetadata vive nel composable perché deve sopravvivere alla chiusura
// di questo dialogo, che avviene subito dopo l'invio.
const { addUrlDialog, seguiJobMetadata } = useAppMenu();
// Auth composable (isDev enables debug info display)
const { isDev } = useLogin();
// Latest episodes composable (getLatest refreshes the latest list after adding a URL)
const { getLatest } = useLatest();

// ---- Reactive state ----

// The URL entered by the user in the text field
const downloadUrl = ref(null);
// Whether the form submission API call is in progress (shows loading spinner)
const isLoading = ref(false);
// Controls visibility of the error snackbar
const showError = ref(false);
// Error message text displayed in the error snackbar
const errorMessage = ref("");
// Template ref for the v-form element, used to trigger validation programmatically
const downloadForm = ref(null);
// Array of recent download objects: { url: string, date: string }
const recentDownloads = ref([]);

// Podcast name (currently unused in form but passed to API)
const podcastName = ref(null);
// Tracks the form's overall validation state via v-model
const valid = ref(false);

// General-purpose snackbar state object for success/info messages
const snackbar = ref({
  show: false,
  message: null,
  color: "info",
});

// Captures the initial value of downloadUrl to reset the form after submission
const initialFormState = downloadUrl.value;

// Controls visibility of the "Clear All" confirmation dialog
const showClearDialog = ref(false);

// On mount, load the recent downloads history from localStorage
onMounted(() => {
  loadRecentDownloads();
});

/**
 * Reads text from the system clipboard and fills the URL input field.
 * Uses the Clipboard API (requires HTTPS or localhost).
 */
const pasteClipboard = async () =>
  (downloadUrl.value = await navigator.clipboard.readText());

/**
 * Validates the form and submits the URL to the server via episodesApi.addUrl().
 * On success: saves URL to recent history, refreshes latest episodes list, resets form.
 * On failure: shows error snackbar with the server error message.
 */
const startDownload = async () => {
  const { valid } = await downloadForm.value.validate();
  if (!valid) return;

  isLoading.value = true;
  // Copia PRIMA che il form venga azzerato più sotto: serve al messaggio di
  // errore differito, che altrimenti direbbe "non sono riuscito ad aggiungere"
  // senza dire cosa.
  const urlInviato = downloadUrl.value;

  try {
    const response = await episodesApi.addUrl(
      downloadUrl.value,
      podcastName.value,
    );

    // Persist this URL in the recent downloads localStorage history
    saveRecentDownloads(downloadUrl.value);

    // Refresh the latest list to show newly added URL
    await getLatest();

    // Reset form back to initial empty state
    downloadUrl.value = initialFormState;

    // Close the parent dialog after successful submission
    addUrlDialog.value = false;

    // Accodato, non ancora estratto: /add-url risponde prima che il lavoro
    // inizi. Si segue il job in sottofondo e si avvisa SOLO se fallisce —
    // altrimenti l'unico segnale di un URL rifiutato era la sua assenza da
    // Latest, senza alcuna spiegazione. Volutamente non atteso: il dialogo
    // deve chiudersi subito.
    seguiJobMetadata(response?.jobId, urlInviato);
  } catch (error) {
    errorMessage.value =
      error.response?.data?.message || "Failed to start download";
    showError.value = true;
  } finally {
    isLoading.value = false;
  }
};

/**
 * Removes a single URL from the recent downloads list by matching URL string.
 * Also enforces the max 5 items limit and persists changes to localStorage.
 */
const removeRecentItem = (url) => {
  const existingIndex = recentDownloads.value.findIndex(
    (item) => item.url === url,
  );
  if (existingIndex >= 0) {
    recentDownloads.value.splice(existingIndex, 1);
  }

  // Enforce maximum of 5 items in history
  if (recentDownloads.value.length > 5) {
    recentDownloads.value = recentDownloads.value.slice(0, 5);
  }
  // Persist updated list (no new URL to add, just saving current state)
  saveRecentDownloads();
};

/**
 * Loads the recent downloads array from localStorage on component mount.
 * Silently falls back to empty array if JSON parsing fails.
 */
const loadRecentDownloads = () => {
  const saved = localStorage.getItem("recentDownloads");
  if (saved) {
    try {
      recentDownloads.value = JSON.parse(saved);
    } catch {
      recentDownloads.value = [];
    }
  }
};

/**
 * Clears the entire recent downloads list and closes the confirmation dialog.
 */
const clearAllRecentDownloads = () => {
  recentDownloads.value = [];
  saveRecentDownloads();
  showClearDialog.value = false;
};

/**
 * Saves the recent downloads list to localStorage.
 * If a URL is provided, it is added to the top (most recent first).
 * Duplicates are removed before re-inserting at position 0.
 * Maximum 5 items are kept.
 * @param {string} [url] - Optional URL to prepend to the list before saving.
 */
const saveRecentDownloads = (url) => {
  if (url) {
    // Remove existing entry for this URL to avoid duplicates
    const existingIndex = recentDownloads.value.findIndex(
      (item) => item.url === url,
    );

    if (existingIndex >= 0) {
      recentDownloads.value.splice(existingIndex, 1);
    }

    // Prepend the new entry at the top of the list
    recentDownloads.value.unshift({
      url,
      date: new Date().toISOString(),
    });

    // Enforce max 5 items
    if (recentDownloads.value.length > 5) {
      recentDownloads.value = recentDownloads.value.slice(0, 5);
    }
  }

  // Persist to localStorage
  localStorage.setItem(
    "recentDownloads",
    JSON.stringify(recentDownloads.value),
  );
};

/**
 * Opens the "Clear All" confirmation dialog.
 */
const confirmClearAll = () => {
  showClearDialog.value = true;
};

/**
 * Copies a URL to the system clipboard, fills the URL input, and shows a snackbar.
 * @param {string} url - The URL to copy.
 */
const copyUrl = (url) => {
  navigator.clipboard.writeText(url);
  // Also fill the input field with the copied URL for convenience
  downloadUrl.value = url;
  snackbar.value.message = "URL copied to clipboard";
  snackbar.value.show = true;
};

/**
 * Vuetify validation rules for the URL input field.
 * Checks: required, valid URL format, and supported source domain.
 */
const urlRules = [
  (value) => {
    if (!value) return "URL is required";
    if (!isValidUrl(value)) return "Invalid URL";
    if (!isSupportedUrl(value))
      return "Only YouTube, Rumble, and Twitter URLs are supported";
    return true;
  },
];

/**
 * Validates that a string is a well-formed URL using the URL constructor.
 * @param {string} string - The string to validate.
 * @returns {boolean} True if the string is a valid URL.
 */
function isValidUrl(string) {
  try {
    new URL(string);
    return true;
  } catch (_) {
    return false;
  }
}

/**
 * Checks whether the URL is from a supported source.
 * Currently returns true for all URLs (domain filtering is commented out).
 * @param {string} url - The URL to check.
 * @returns {boolean} Always true (placeholder for future domain filtering).
 */
function isSupportedUrl(url) {
  return true;
  // if (url) {
  //   return (
  //     url.includes("youtube.com") ||
  //     url.includes("youtu.be") ||
  //     url.includes("x.com") ||
  //     url.includes("twitter.com") ||
  //     url.includes("rumble.com")
  //   );
  // }
  // return false;
}
</script>
